import type { TFunction } from 'i18next';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, useWindowDimensions, View } from 'react-native';

import {
	appDeepLink,
	appStoreUrl,
	detectInvitePlatform,
	playStoreUrl,
} from '../../../../services/invite/storeLinks';
import { goldGlow } from '../../../../shared/glowStyles';
import { Pressable } from '../../common/Touchable';
import { inventoryColors } from '../../Inventory/inventoryTheme';
import { FRIENDS_FONT, friendsColors, hairline, RADIUS_LG, RADIUS_MD } from '../friendsTheme';
import { IconPeople } from './inviteFriendsIcons';
import { copyToClipboard } from './shareInvite';

/**
 * The spec's "Landing / App Open" step, on the web build: a phone that opens an invite link
 * (`<inviteBaseUrl>/<code>`) lands here instead of on the lobby.
 *
 * - Android: "Get it on Google Play" opens the listing with `referrer=invite_code=<code>`; the app
 *   reads that back on its first launch, so the friend is attributed when they sign up.
 * - iOS: "Download on the App Store" first copies the invite link - the tap is the user gesture
 *   the clipboard needs - and the app picks it up from the pasteboard on first launch.
 * - "I already have the app" opens it through the `svara://invite/<code>` scheme.
 * - "Continue in browser" keeps the code for signing up right here on the web.
 *
 * Desktop browsers skip this and go straight to the lobby with the code kept.
 */
const getCopy = (t: TFunction) => ({
	title: t('T_SITE_INVITE_LANDING_TITLE', { defaultValue: 'You’ve been invited!' }),
	body: t('T_SITE_INVITE_LANDING_BODY', {
		defaultValue:
			'A friend invited you to Svara. Get the app and sign up to join them at the table.',
	}),
	play: t('T_SITE_INVITE_LANDING_GOOGLE_PLAY', { defaultValue: 'Get it on Google Play' }),
	appStore: t('T_SITE_INVITE_LANDING_APP_STORE', { defaultValue: 'Download on the App Store' }),
	openApp: t('T_SITE_INVITE_LANDING_HAVE_APP', { defaultValue: 'I already have the app' }),
	browser: t('T_SITE_INVITE_LANDING_CONTINUE_BROWSER', { defaultValue: 'Continue in browser' }),
	code: (code: string) =>
		t('T_SITE_INVITE_LANDING_CODE', { defaultValue: 'Invite code: {{code}}', code }),
});

type Props = {
	code: string;
	onContinue: () => void;
};

export const InviteLanding = ({ code, onContinue }: Props) => {
	const { width } = useWindowDimensions();
	const scale = Math.min(width / 390, 1.15);
	const styles = useMemo(() => createStyles(scale), [scale]);
	const { t } = useTranslation();
	const copy = useMemo(() => getCopy(t), [t]);
	const platform = detectInvitePlatform();
	const iosStore = appStoreUrl();

	if (platform === 'desktop') return null;

	const goTo = (url: string) => {
		window.location.href = url;
	};

	const openStore = async () => {
		if (platform === 'android') {
			goTo(playStoreUrl(code));
			return;
		}
		if (!iosStore) return;
		await copyToClipboard(`${window.location.origin}/invite/${encodeURIComponent(code)}`);
		goTo(iosStore);
	};

	const showStore = platform === 'android' || !!iosStore;

	return (
		<View style={styles.root}>
			<View style={styles.card}>
				<View style={styles.icon}>
					<IconPeople size={44 * scale} />
				</View>
				<Text style={styles.title}>{copy.title}</Text>
				<Text style={styles.body}>{copy.body}</Text>
				<Text style={styles.code}>{copy.code(code)}</Text>

				{showStore ? (
					<Pressable
						accessibilityRole='button'
						onPress={openStore}
						style={[styles.button, styles.primary]}
					>
						<Text style={[styles.buttonLabel, styles.primaryLabel]}>
							{platform === 'android' ? copy.play : copy.appStore}
						</Text>
					</Pressable>
				) : null}

				<Pressable
					accessibilityRole='button'
					onPress={() => goTo(appDeepLink(code))}
					style={styles.button}
				>
					<Text style={styles.buttonLabel}>{copy.openApp}</Text>
				</Pressable>

				<Pressable accessibilityRole='button' onPress={onContinue} style={styles.link}>
					<Text style={styles.linkLabel}>{copy.browser}</Text>
				</Pressable>
			</View>
		</View>
	);
};

const createStyles = (scale: number) =>
	StyleSheet.create({
		root: {
			...StyleSheet.absoluteFillObject,
			alignItems: 'center',
			justifyContent: 'center',
			backgroundColor: inventoryColors.scrim,
			zIndex: 1000,
			padding: 16 * scale,
		},
		card: {
			width: 340 * scale,
			maxWidth: '100%',
			alignItems: 'center',
			gap: 12 * scale,
			padding: 20 * scale,
			borderRadius: RADIUS_LG * scale,
			borderWidth: hairline(scale),
			borderColor: inventoryColors.panelBorder,
			backgroundColor: friendsColors.surface,
		},
		icon: {
			width: 72 * scale,
			height: 72 * scale,
			alignItems: 'center',
			justifyContent: 'center',
			borderRadius: RADIUS_LG * scale,
			borderWidth: hairline(scale),
			borderColor: friendsColors.borderGold,
			backgroundColor: friendsColors.surfaceSunken,
		},
		title: {
			textAlign: 'center',
			color: friendsColors.accent,
			fontFamily: FRIENDS_FONT,
			fontSize: 24 * scale,
			fontWeight: '900',
			lineHeight: 30 * scale,
		},
		body: {
			textAlign: 'center',
			color: friendsColors.gold50,
			fontFamily: FRIENDS_FONT,
			fontSize: 14 * scale,
			fontWeight: '500',
			lineHeight: 20 * scale,
		},
		code: {
			color: friendsColors.muted,
			fontFamily: FRIENDS_FONT,
			fontSize: 12 * scale,
			fontWeight: '700',
		},
		button: {
			alignSelf: 'stretch',
			height: 46 * scale,
			alignItems: 'center',
			justifyContent: 'center',
			borderRadius: RADIUS_MD * scale,
			borderWidth: hairline(scale),
			borderColor: friendsColors.borderGold,
			backgroundColor: friendsColors.surfaceSunken,
			...goldGlow,
		},
		primary: {
			backgroundColor: friendsColors.gold300,
			borderColor: friendsColors.gold50,
		},
		buttonLabel: {
			color: friendsColors.gold50,
			fontFamily: FRIENDS_FONT,
			fontSize: 15 * scale,
			fontWeight: '900',
		},
		primaryLabel: {
			color: friendsColors.onGold,
		},
		link: {
			paddingVertical: 6 * scale,
		},
		linkLabel: {
			color: friendsColors.muted,
			fontFamily: FRIENDS_FONT,
			fontSize: 13 * scale,
			fontWeight: '700',
			textDecorationLine: 'underline',
		},
	});

export default InviteLanding;
